import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import Characters from './Characters';
import Conversations from './Conversations';
import FolderManager from './FolderManager';

const SidebarContainer = styled.div`
  width: 320px;
  height: 100%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(249, 252, 255, 0.1) 100%);
  color: var(--text-color);
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  padding: var(--space-5) var(--space-4);
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  /* Modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: var(--radius-full);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  .dark-mode & {
    background: linear-gradient(180deg, rgba(30, 30, 30, 0.9) 0%, rgba(18, 18, 18, 0.8) 100%);
    border-right-color: rgba(255, 255, 255, 0.1);
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
    }
    &::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const SidebarSection = styled.div`
  margin-bottom: var(--space-5);
`;

const ProductStudioButton = styled.button`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.1), rgba(74, 144, 226, 0.05));
  border: 1px solid rgba(74, 144, 226, 0.3);
  border-radius: var(--radius-lg);
  color: var(--text-color);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: var(--space-4);

  &:hover {
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.2), rgba(74, 144, 226, 0.1));
    border-color: rgba(74, 144, 226, 0.5);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  .dark-mode & {
    background: linear-gradient(135deg, rgba(187, 134, 252, 0.1), rgba(187, 134, 252, 0.05));
    border-color: rgba(187, 134, 252, 0.3);

    &:hover {
      background: linear-gradient(135deg, rgba(187, 134, 252, 0.2), rgba(187, 134, 252, 0.1));
      border-color: rgba(187, 134, 252, 0.5);
      box-shadow: 0 4px 12px rgba(187, 134, 252, 0.2);
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: var(--text-sm);
  margin-bottom: var(--space-4);
  color: var(--text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: var(--font-heading-medium);
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);

  &::before {
    content: '';
    width: 4px;
    height: 16px;
    background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border-color-light);
  margin: var(--space-4) 0;
`;

function Sidebar({ isOpen, userId, projectId, onFileUpload, onFolderSelect, onConversationSelect, onCharacterEdit, selectedFolder, onProductStudioOpen }) {
  return (
    <SidebarContainer style={{
      width: isOpen ? '320px' : '0',
      transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
    }}>
      <ProductStudioButton onClick={onProductStudioOpen}>
        <FontAwesomeIcon icon={faImage} />
        Product Studio
      </ProductStudioButton>
      <Divider />
      <SidebarSection>
        <SectionTitle>Folders</SectionTitle>
        <FolderManager
          userId={userId}
          onFolderSelect={onFolderSelect}
        />
      </SidebarSection>
      <Divider />
      <SidebarSection>
        <SectionTitle>Characters</SectionTitle>
        <Characters onCharacterEdit={onCharacterEdit} />
      </SidebarSection>
      <Divider />
      <SidebarSection>
        <SectionTitle>Conversations</SectionTitle>
        <Conversations
          userId={userId}
          projectId={projectId}
          selectedFolder={selectedFolder}
          onConversationSelect={onConversationSelect}
        />
      </SidebarSection>
    </SidebarContainer>
  );
}

export default Sidebar;